/**
 * Hook para buscar distribuição de tarefas por tipo
 * 
 * Utiliza React Query para cache e gerenciamento de estado assíncrono.
 * Retorna dados formatados para gráfico de pizza.
 * 
 * @module hooks/data/useTaskDistribution
 */

import { useQuery } from '@tanstack/react-query';
import { getTaskDistributionApi } from '../../api/api';
import { logHttpError } from '../../utils/errorLogger';
import { getErrorMessage } from '../../utils/errorMessages';

// Query key para cache do React Query
export const TASK_DISTRIBUTION_QUERY_KEY = ['dashboard', 'task-distribution'];

/**
 * Hook para buscar distribuição de tarefas por tipo
 * 
 * Configurações do React Query:
 * - staleTime: 5 minutos (dados considerados frescos)
 * - gcTime: 10 minutos (tempo de cache após desmontagem)
 * - retry: 3 tentativas com backoff exponencial
 * 
 * @returns {object} Objeto com dados, estados e funções
 * @property {Array} data - Array de objetos com tipo_tarefa e total
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {string|null} error - Mensagem de erro (null se não houver erro)
 * @property {function} refetch - Função para recarregar os dados manualmente
 * 
 * @example
 * ```javascript
 * const { data, isLoading, error } = useTaskDistribution();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <Chart data={data} />;
 * ```
 */
export const useTaskDistribution = () => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: TASK_DISTRIBUTION_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await getTaskDistributionApi();
                // Garante que retorna um array, mesmo se vier vazio
                return response.data?.data || [];
            } catch (err) {
                logHttpError(err, {
                    component: 'useTaskDistribution',
                    endpoint: '/dashboard/task-distribution',
                });
                throw err;
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados frescos
        gcTime: 1000 * 60 * 10, // 10 minutos - tempo de cache
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Manter compatibilidade: retornar error como string (se houver)
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'taskDistribution',
        })
        : null;

    return {
        data: data || [],
        isLoading,
        isError,
        error: errorMessage,
        refetch,
    };
};
